import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { REGIONES, ESPECIALIDADES_MED } from "@/lib/constants";
import { BadgeCheck, MapPin, Star, Search, HeartHandshake, ArrowRight, Send, Building2, Video } from "lucide-react";
import { useConfig } from "@/contexts/ConfigContext";

export default function Mediators() {
  const [params, setParams] = useSearchParams();
  const { config } = useConfig();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({
    region: params.get("region") || "",
    comuna: params.get("comuna") || "",
    specialty: params.get("specialty") || "",
    q: params.get("q") || "",
  });

  const load = async () => {
    const q = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
    const { data } = await api.get("/mediators", { params: q });
    setItems(data.items);
    setParams(q);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="container-app py-12" data-testid="mediators-page">
      <div className="label-eyebrow">Directorio nacional</div>
      <h1 className="serif text-5xl mt-3">Mediadores familiares</h1>
      <p className="mt-2 text-muted-foreground max-w-2xl">Profesionales inscritos en el Registro de Mediadores del Ministerio de Justicia. Filtra por región, comuna y especialidad.</p>

      {config.new_mediation_enabled && (
        <Link to="/mediacion-familiar" className="mt-6 panel p-4 border-cyan-400/25 hover:bg-cyan-400/5 transition-colors flex items-center gap-3 group max-w-3xl" data-testid="mediators-mediacion-familiar-cta">
          <HeartHandshake size={20} className="text-cyan-300 shrink-0"/>
          <div className="grow text-sm">
            <span className="text-foreground font-medium">¿Buscas contratar una mediación familiar privada?</span>
            <span className="text-muted-foreground"> Directorio dedicado con tarifario y filtros de modalidad presencial / telemática.</span>
          </div>
          <ArrowRight size={14} className="text-cyan-300 group-hover:translate-x-0.5 transition-transform"/>
        </Link>
      )}

      <div className="mt-8 panel p-5 grid sm:grid-cols-5 gap-3">
        <div className="sm:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <input className="field pl-9" placeholder="Buscar por nombre o palabra clave" value={filters.q} onChange={(e)=>setFilters({...filters, q:e.target.value})} data-testid="med-search"/>
        </div>
        <select className="field" value={filters.region} onChange={(e)=>setFilters({...filters, region:e.target.value})} data-testid="med-region">
          <option value="">Todas las regiones</option>
          {REGIONES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <input className="field" placeholder="Comuna" value={filters.comuna} onChange={(e)=>setFilters({...filters, comuna:e.target.value})} data-testid="med-comuna"/>
        <select className="field" value={filters.specialty} onChange={(e)=>setFilters({...filters, specialty:e.target.value})} data-testid="med-specialty">
          <option value="">Toda especialidad</option>
          {ESPECIALIDADES_MED.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={load} className="btn-primary sm:col-span-5 sm:justify-self-start" data-testid="med-apply">Aplicar</button>
      </div>

      <ul className="mt-8 grid md:grid-cols-2 gap-4" data-testid="mediators-list">
        {items.length === 0 && <li className="panel p-10 text-center text-muted-foreground md:col-span-2">Sin resultados.</li>}
        {items.map((m) => {
          const canSolicit = (user?.role === "lawyer" || user?.role === "requester") && m.verified && m.mediator_directory_complete;
          return (
          <li key={m.id} className="panel p-6 hover:border-foreground/40 transition-colors" data-testid={`mediator-card-${m.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/app/mediadores/${m.id}`} className="serif text-2xl link-underline">{m.name}</Link>
                  {m.verified && <span className="pill badge-verified"><BadgeCheck size={12}/>Verificado</span>}
                </div>
                <div className="mt-1 text-sm text-muted-foreground inline-flex items-center gap-1.5"><MapPin size={13}/>{m.comuna}, {m.region}</div>
                {(m.attends_presencial || m.attends_remote) && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`mediator-modalities-${m.id}`}>
                    {m.attends_presencial && (
                      <span className="pill text-[10px] inline-flex items-center gap-1 border-cyan-400/40 bg-cyan-400/10 text-cyan-200"><Building2 size={10}/> Presencial</span>
                    )}
                    {m.attends_remote && (
                      <span className="pill text-[10px] inline-flex items-center gap-1 border-cyan-400/40 bg-cyan-400/10 text-cyan-200"><Video size={10}/> Telemática</span>
                    )}
                  </div>
                )}
                {m.bio && <p className="mt-3 text-sm text-foreground/80 line-clamp-2">{m.bio}</p>}
                <div className="mt-3 flex flex-wrap gap-2">
                  {(m.specialties || []).map((s) => <span key={s} className="pill">{s}</span>)}
                </div>
              </div>
              <div className="text-right shrink-0">
                {m.reputation > 0 && (
                  <div className="inline-flex items-center gap-1 serif text-2xl">
                    <Star size={16} className="text-[hsl(var(--primary))]"/>{m.reputation}
                  </div>
                )}
                <div className="label-eyebrow mt-1">{m.reviews_count || 0} reseñas</div>
                {m.registry_number && <div className="text-xs text-muted-foreground mt-2">{m.registry_number}</div>}
              </div>
            </div>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <Link to={`/app/mediadores/${m.id}`} className="btn-ghost text-sm inline-flex items-center gap-1.5" data-testid={`mediator-view-${m.id}`}>
                Ver perfil <ArrowRight size={12}/>
              </Link>
              {canSolicit && (
                <Link
                  to={`/app/mediadores/${m.id}?solicitar=1`}
                  className="btn-primary text-sm inline-flex items-center gap-1.5"
                  data-testid={`mediator-solicit-${m.id}`}
                >
                  <Send size={12}/> Solicitar Mediación
                </Link>
              )}
            </div>
          </li>
          );
        })}
      </ul>
    </div>
  );
}
